import {
  DRAFT_KEY,
  defaultValues,
  goodsTypeOptions,
  handlingUnitOptions,
  insuranceCompanyOptions,
  statusOptions,
  unitOptions,
  vehicleTypeOptions,
} from "./loadBooking.constants";

const clone = (value) => JSON.parse(JSON.stringify(value));

export const safeArray = (value) => (Array.isArray(value) ? value : []);
export const normalize = (value) => String(value || "").trim().toLowerCase();
export const unique = (value) => [...new Set(safeArray(value).filter(Boolean))];
export const titleCase = (value) => {
  const text = String(value || "").replace(/_/g, " ");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "-";
};
export const percent = (part, total) =>
  total ? `${((part / total) * 100).toFixed(1)}%` : "0%";

const cleanText = (value) => String(value || "").trim();
const stripHtml = (value) =>
  cleanText(
    String(value || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/\s+/g, " ")
  );
const toOptionalNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};
const toOptionalPositiveId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};
const resolveUploadedFilePath = (file) => {
  if (typeof file === "string") return cleanText(file);
  if (!file || typeof file !== "object") return "";
  return cleanText(
    file?.file_path ||
      file?.fileName ||
      file?.file_name ||
      file?.path ||
      file?.url ||
      file?.file ||
      file?.image ||
      file?.originalName ||
      ""
  );
};
const toDateOnly = (value) => {
  const text = cleanText(value);
  if (!text) return "";
  return text.split("T")[0];
};
const resolveOptionIdMap = (options) => {
  const map = new Map();

  safeArray(options).forEach((item, index) => {
    const fallbackId = index + 1;
    const resolvedId =
      toOptionalPositiveId(item?.id) ||
      toOptionalPositiveId(item?.value) ||
      fallbackId;

    map.set(normalize(item?.label), resolvedId);
    map.set(normalize(item?.value), resolvedId);
  });

  return map;
};

const goodsIdMap = resolveOptionIdMap(goodsTypeOptions);
const qtyUnitIdMap = resolveOptionIdMap(handlingUnitOptions);
const weightUnitIdMap = resolveOptionIdMap(unitOptions);
const insuranceCompanyIdMap = resolveOptionIdMap(insuranceCompanyOptions);
const vehicleTypeIdMap = resolveOptionIdMap(vehicleTypeOptions);

const resolveMappedId = (value, map) => {
  const direct = toOptionalPositiveId(value);
  if (direct) return direct;
  return map.get(normalize(value)) || null;
};

export const readStorage = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

export const writeStorage = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const loadBookings = () => {
  return [];
};

export const saveBookings = () => {};
export const clearLoadBookingDraft = () => localStorage.removeItem(DRAFT_KEY);

export const touchedFromErrors = (errors) => {
  if (Array.isArray(errors)) return errors.map((item) => touchedFromErrors(item));
  if (errors && typeof errors === "object") {
    return Object.keys(errors).reduce((acc, key) => {
      acc[key] = touchedFromErrors(errors[key]);
      return acc;
    }, {});
  }
  return true;
};

const filterField = (row, filterName) => {
  switch (filterName) {
    case "idFilter":
      return row.id;
    case "customerNameFilter":
      return row.customerName;
    case "transporterNameFilter":
      return row.transporterName;
    case "loadingPointFilter":
      return row.loadingPoint;
    case "offloadingPointFilter":
      return row.offloadingPoint;
    case "expectedDeliveryDateFilter":
      return row.expectedDeliveryDate;
    case "statusFilter":
      return row.status;
    default:
      return "";
  }
};

const matchesFilter = (rawValue, filterObj) => {
  const type = filterObj?.filter_type;
  const value = filterObj?.value;

  if (
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && !value.length)
  ) {
    return true;
  }

  if (type === "one_of") {
    const selected = (Array.isArray(value) ? value : [value]).map(normalize);
    if (Array.isArray(rawValue)) {
      return rawValue.some((item) => selected.includes(normalize(item)));
    }
    return selected.includes(normalize(rawValue));
  }

  const leftText = normalize(Array.isArray(rawValue) ? rawValue.join(" ") : rawValue);
  const rightText = normalize(value);

  if (type === "contains") return leftText.includes(rightText);
  if (type === "equals" || type === "equal_to") return leftText === rightText;

  return true;
};

export const applyFilters = (rows, searchFilters) => {
  if (!searchFilters || !Object.keys(searchFilters).length) return rows;
  return rows.filter((row) =>
    Object.entries(searchFilters).every(([name, filterObj]) =>
      matchesFilter(filterField(row, name), filterObj)
    )
  );
};

const allowedStatus = new Set(statusOptions.map((item) => item.value));

export const statusMeta = (status) => {
  const normalized = normalize(status);
  if (normalized === "active") return { color: "#299900", bg: "#ECF8E9" };
  if (normalized === "inactive") return { color: "#EF0000", bg: "#FCECEC" };
  if (normalized === "in_transit") return { color: "#F2841B", bg: "#FFF4E8" };
  if (normalized === "delayed") return { color: "#8B8EAB", bg: "#F2F3FA" };
  if (normalized === "completed") return { color: "#299900", bg: "#ECF8E9" };
  return { color: "#3D3F5C", bg: "transparent" };
};

export const generateId = (rows = []) => {
  const currentMax = rows.reduce((max, item) => {
    const match = String(item?.id || "").match(/LDB-(\d+)/i);
    if (!match) return max;
    return Math.max(max, Number(match[1]));
  }, 1000);

  return `LDB-${String(currentMax + 1).padStart(4, "0")}`;
};

export const toFormValues = (record) => {
  const base = defaultValues();
  if (!record) return base;

  const data = record?.formData ? clone(record.formData) : {};
  const merged = {
    ...base,
    ...data,
    loadId: record.id || data.loadId || "",
    customer: data.customer || normalize(record.customerName) || "",
    transporterName: data.transporterName || record.transporterName || "",
    vehicleType: data.vehicleType || "",
    loadingPoint: data.loadingPoint || record.loadingPoint || "",
    offloadingPoint: data.offloadingPoint || record.offloadingPoint || "",
    departureDateTime: data.departureDateTime || "",
    deliveryDateTime:
      data.deliveryDateTime ||
      (record.expectedDeliveryDate ? `${record.expectedDeliveryDate}T10:00` : ""),
    goods: safeArray(data.goods).length ? safeArray(data.goods) : base.goods,
    isInsured: Boolean(data.isInsured),
    insurance: { ...base.insurance, ...(data.insurance || {}) },
    attachments: safeArray(data.attachments),
    payment: { ...base.payment, ...(data.payment || {}) },
    notes: data.notes || record.notes || base.notes,
    shareWith: { ...base.shareWith, ...(data.shareWith || {}) },
  };

  return merged;
};

export const formToRow = (values, id, previousStatus = "inactive") => {
  const cleanedStatus = normalize(previousStatus);
  const resolvedStatus = allowedStatus.has(cleanedStatus)
    ? cleanedStatus
    : "inactive";

  return {
    id,
    customerName: cleanText(values.customerName || values.customer || "-"),
    transporterName: cleanText(values.transporterName),
    loadingPoint: cleanText(values.loadingPointLabel || values.loadingPoint),
    offloadingPoint: cleanText(values.offloadingPointLabel || values.offloadingPoint),
    expectedDeliveryDate: cleanText(values.expectedDeliveryDate),
    status: resolvedStatus,
    attachmentsCount: safeArray(values.attachments).length,
    updatedAt: new Date().toISOString(),
    formData: clone(values),
  };
};

export const toCreateLoadPayload = (values) => {
  const customerId = toOptionalPositiveId(values?.customerId ?? values?.customer);
  const transporterId = toOptionalPositiveId(values?.transporterId);
  const loadingWarehouseId = toOptionalPositiveId(values?.loadingWarehouseId);
  const offloadingWarehouseId = toOptionalPositiveId(values?.offloadingWarehouseId);

  const vehicleTypeId = resolveMappedId(values?.vehicleType, vehicleTypeIdMap);
  const insuranceCompanyId = resolveMappedId(
    values?.insurance?.company,
    insuranceCompanyIdMap
  );

  const normalizedGoods = safeArray(values?.goods)
    .map((item) => ({
      good_id: resolveMappedId(item?.goodType, goodsIdMap),
      qty_unit_id: resolveMappedId(item?.handlingUnit, qtyUnitIdMap),
      qty_unit: toOptionalNumber(item?.quantity),
      weight_unit_id: resolveMappedId(item?.unit, weightUnitIdMap),
      weight: toOptionalNumber(item?.weight),
      hs_code: cleanText(item?.hsCode),
      goods_price:
        toOptionalNumber(item?.price) ?? toOptionalNumber(values?.payment?.sellingPrice),
    }))
    .filter(
      (item) =>
        item.good_id ||
        item.qty_unit_id ||
        item.qty_unit !== null ||
        item.weight_unit_id ||
        item.weight !== null ||
        item.hs_code ||
        item.goods_price !== null
    );

  const loadStopsAndGoods = [];
  normalizedGoods.forEach((item) => {
    if (loadingWarehouseId) {
      loadStopsAndGoods.push({
        customer_warehouse_id: loadingWarehouseId,
        stop_number: 1,
        stop_type: "loading",
        ...item,
      });
    }
    if (offloadingWarehouseId) {
      loadStopsAndGoods.push({
        customer_warehouse_id: offloadingWarehouseId,
        stop_number: 2,
        stop_type: "offloading",
        ...item,
      });
    }
  });

  const transporterNotify =
    transporterId &&
    (Boolean(values?.shareWith?.email) || Boolean(values?.shareWith?.whatsapp))
      ? [
          {
            transporter_id: transporterId,
            email: Boolean(values?.shareWith?.email),
            whatsapp: Boolean(values?.shareWith?.whatsapp),
          },
        ]
      : [];

  const payload = {
    customer_id: customerId,
    departure_at: toDateOnly(values?.departureDateTime),
    delivered_at: toDateOnly(values?.deliveryDateTime),
    insured: Boolean(values?.isInsured),
    buying_price: toOptionalNumber(values?.payment?.buyingPrice),
    selling_price: cleanText(values?.payment?.sellingPrice),
    payment_terms: cleanText(values?.payment?.paymentTerm),
    billing_frequency: cleanText(values?.payment?.billingFrequency),
    payment_method: cleanText(values?.payment?.paymentMode),
    bank_name: cleanText(values?.payment?.bank),
    account_title: cleanText(values?.payment?.accountTitle),
    iban_number: cleanText(values?.payment?.iban),
    notes: stripHtml(values?.notes),
    vehicle_type_ids: vehicleTypeId ? [vehicleTypeId] : [],
    files: unique(safeArray(values?.attachments).map(resolveUploadedFilePath).filter(Boolean)),
    insurance_company_id: insuranceCompanyId,
    insurance_email: cleanText(values?.insurance?.email),
    insurance_policy_number: cleanText(values?.insurance?.policyNumber),
    insurance_contact: cleanText(values?.insurance?.contactNumber),
    insurance_good_price: toOptionalNumber(values?.insurance?.goodsPrice),
    cover_amount: toOptionalNumber(values?.insurance?.coverageAmount),
    transporter_notify: transporterNotify,
    load_stops_and_goods: loadStopsAndGoods,
  };

  if (!payload.insured) {
    delete payload.insurance_company_id;
    delete payload.insurance_email;
    delete payload.insurance_policy_number;
    delete payload.insurance_contact;
    delete payload.insurance_good_price;
    delete payload.cover_amount;
  }

  return payload;
};

export const prepareSnapshot = (values) => {
  const goods = safeArray(values.goods).map((item) => ({
    goodType: cleanText(item?.goodType),
    quantity: cleanText(item?.quantity),
    handlingUnit: cleanText(item?.handlingUnit),
    weight: cleanText(item?.weight),
    hsCode: cleanText(item?.hsCode),
    price: cleanText(item?.price),
    unit: cleanText(item?.unit),
  }));

  return {
    goods,
    notes: stripHtml(values.notes),
  };
};
